import { useEffect, useRef, useState } from "react";
import { Ban, Clock, ExternalLink, Link as LinkIcon, Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import {
  type MatrixActivityKind,
  type MatrixItemActivity,
  type MatrixItemStatus,
  type OrgUserSummary,
  type Priority,
  type RequirementType,
} from "@/lib/api";
import { useMatrixItemActivity } from "@/hooks/useProjects";
import type { ResponseMatrixItem } from "@/hooks/useProjects";
import { PriorityBadge, TypeBadge } from "@/components/ui/Badges";
import { Avatar, UserPickerMenu, userLabel } from "@/components/UserPicker";

// ---------------------------------------------------------------------------
// Type-aware status options (kept in sync with ResponseMatrix.tsx). Wire enum
// stays the same; user-facing labels adapt to the row's type.
// ---------------------------------------------------------------------------
function statusOptionsForType(type: RequirementType): { value: MatrixItemStatus; label: string }[] {
  switch (type) {
    case "compliance":
      return [
        { value: "not_started", label: "Pending" },
        { value: "complete", label: "Acknowledged" },
      ];
    case "admin":
      return [
        { value: "not_started", label: "Pending" },
        { value: "in_progress", label: "Working on it" },
        { value: "complete", label: "Done" },
      ];
    case "pricing":
      return [
        { value: "not_started", label: "Pending" },
        { value: "in_progress", label: "Pricing" },
        { value: "draft_ready", label: "Priced" },
        { value: "in_review", label: "In Review" },
        { value: "complete", label: "Submitted" },
      ];
    default:
      return [
        { value: "not_started", label: "Pending" },
        { value: "in_progress", label: "Drafting" },
        { value: "draft_ready", label: "Draft Ready" },
        { value: "in_review", label: "In Review" },
        { value: "complete", label: "Final" },
      ];
  }
}

const DECISION_NOTES_DEBOUNCE_MS = 1200;

// ---------------------------------------------------------------------------
// Side panel
// ---------------------------------------------------------------------------

interface SidePanelProps {
  item: ResponseMatrixItem;
  orgUsers: OrgUserSummary[];
  usersById: Map<string, OrgUserSummary>;
  myId: string | null;
  onClose: () => void;
  onPatch: (patch: Partial<ResponseMatrixItem>) => void;
  onDelete: () => void;
}

export function MatrixItemSidePanel({
  item,
  orgUsers,
  usersById,
  myId,
  onClose,
  onPatch,
  onDelete,
}: SidePanelProps) {
  // ----- Decision notes: local draft + debounced autosave -----
  const [notesDraft, setNotesDraft] = useState(item.notes ?? "");
  const [notesSaved, setNotesSaved] = useState(true);
  const lastServerNotesRef = useRef(item.notes ?? "");

  // If the row's notes change underneath us (e.g., another teammate edits),
  // accept it ONLY if the local draft equals the previously known server
  // value. Otherwise the user is mid-typing and we'd clobber their input.
  useEffect(() => {
    const incoming = item.notes ?? "";
    if (incoming !== lastServerNotesRef.current && notesDraft === lastServerNotesRef.current) {
      setNotesDraft(incoming);
    }
    lastServerNotesRef.current = incoming;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.notes]);

  useEffect(() => {
    // No save needed if draft matches the last server value.
    if (notesDraft === lastServerNotesRef.current) {
      setNotesSaved(true);
      return;
    }
    setNotesSaved(false);
    const t = setTimeout(() => {
      onPatch({ notes: notesDraft });
      lastServerNotesRef.current = notesDraft;
      setNotesSaved(true);
    }, DECISION_NOTES_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesDraft]);

  // ----- Owner picker -----
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pickerOpen]);

  // ----- Activity log -----
  const { data: activityData, isLoading: activityLoading } = useMatrixItemActivity(item.id);
  const activity: MatrixItemActivity[] = activityData?.activity ?? [];

  // ----- Close on Escape -----
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const owner = item.ownerUserId ? (usersById.get(item.ownerUserId) ?? null) : null;
  const statusOptions = statusOptionsForType(item.type);
  const isNa = item.notApplicable;
  // Synced row: status + owner are managed by the external PM tool. Fall
  // back to legacy `teamworkTaskId` so existing rows still lock.
  const isSynced = Boolean(item.externalTaskId ?? item.teamworkTaskId);

  const handleCopyLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}#row=${item.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste into Slack or Teams.");
    } catch {
      // Fallback for browsers without clipboard API on insecure origins
      toast.info(`Copy this link: ${url}`);
    }
  };

  return (
    <>
      {/* Click-outside backdrop */}
      <div
        className="fixed inset-0 z-30"
        style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className="fixed top-0 right-0 h-full z-40 flex flex-col"
        style={{
          width: 480,
          maxWidth: "92vw",
          backgroundColor: "#0F1419",
          borderLeft: "1px solid rgba(229,231,235,0.08)",
          boxShadow: "-12px 0 40px rgba(0,0,0,0.45)",
        }}
        role="dialog"
        aria-label="Requirement details"
      >
        {/* Header */}
        <header
          className="px-5 py-4 border-b flex items-start gap-3"
          style={{ borderColor: "rgba(229,231,235,0.08)" }}
        >
          <div className="flex-1 min-w-0">
            <p
              className="text-[11px] uppercase tracking-wider font-semibold mb-1.5"
              style={{ color: "#6B7280" }}
            >
              {item.section}
            </p>
            <h2
              className={
                isNa
                  ? "text-base font-semibold leading-snug text-gray-500 line-through"
                  : "text-base font-semibold leading-snug text-white"
              }
            >
              {item.requirement}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5 flex-shrink-0"
            title="Close (Esc)"
          >
            <X size={18} />
          </button>
        </header>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {/* Synced banner — status/owner are read-only when this row is
              linked to an external PM tool. */}
          {isSynced && <SyncedBanner item={item} />}

          {/* Metadata grid */}
          <section className="space-y-3">
            <FieldLabel>Owner</FieldLabel>
            {isSynced ? (
              <div
                className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg border bg-white/[0.02] border-white/10"
                title="Managed in your project-management tool — open the task to reassign."
              >
                <Avatar user={owner} size={22} />
                <span className="text-sm truncate" style={{ color: owner ? "#9CA3AF" : "#6B7280" }}>
                  {owner ? userLabel(owner) : "Unassigned"}
                </span>
                <span className="text-[10px] text-gray-500 ml-auto">via PM tool</span>
              </div>
            ) : (
              <div ref={pickerRef} className="relative">
                <button
                  onClick={() => setPickerOpen((v) => !v)}
                  className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg border bg-white/[0.03] border-white/10 hover:bg-white/[0.06] text-left"
                >
                  <Avatar user={owner} size={22} />
                  <span className="text-sm truncate" style={{ color: owner ? "#fff" : "#9CA3AF" }}>
                    {owner ? userLabel(owner) : "Assign someone"}
                  </span>
                </button>
                {pickerOpen && (
                  <UserPickerMenu
                    users={orgUsers}
                    myId={myId}
                    allowUnassign={Boolean(owner)}
                    onPick={(id) => {
                      setPickerOpen(false);
                      onPatch({ ownerUserId: id });
                    }}
                  />
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>Status</FieldLabel>
                {isNa ? (
                  <div className="px-2.5 py-2 rounded-lg border bg-gray-500/10 text-gray-500 border-gray-500/20 text-sm">
                    Marked N/A
                  </div>
                ) : isSynced ? (
                  <div
                    className="w-full px-2.5 py-2 rounded-lg border bg-white/[0.02] border-white/10 text-sm text-gray-300"
                    title="Status is managed in your project-management tool."
                  >
                    {statusOptions.find((o) => o.value === item.status)?.label ?? item.status}
                  </div>
                ) : (
                  <select
                    value={item.status}
                    onChange={(e) => onPatch({ status: e.target.value as MatrixItemStatus })}
                    className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-2.5 py-2 text-sm text-white outline-none"
                  >
                    {statusOptions.map((o) => (
                      <option key={o.value} value={o.value} style={{ backgroundColor: "#111318" }}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <FieldLabel>Priority</FieldLabel>
                <select
                  value={item.priority}
                  onChange={(e) => onPatch({ priority: e.target.value as Priority })}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-2.5 py-2 text-sm text-white outline-none capitalize"
                >
                  {(["high", "medium", "low"] as Priority[]).map((p) => (
                    <option key={p} value={p} style={{ backgroundColor: "#111318" }}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs">
              <TypeBadge type={item.type} />
              <PriorityBadge priority={item.priority} />
              {isNa && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-500/10 text-gray-500 border border-gray-500/20">
                  N/A
                </span>
              )}
            </div>

            <button
              onClick={() => onPatch({ notApplicable: !item.notApplicable })}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-xs text-gray-300 bg-white/[0.03] border-white/10 hover:bg-white/[0.06] hover:text-white"
            >
              <Ban size={12} />
              {isNa ? "Mark applicable" : "Mark not applicable"}
            </button>
          </section>

          {/* Decision notes */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <FieldLabel>Decision notes</FieldLabel>
              <span className="text-[10px]" style={{ color: notesSaved ? "#22c55e" : "#9CA3AF" }}>
                {notesSaved ? "Saved" : "Saving…"}
              </span>
            </div>
            <textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              placeholder="Why was this acknowledged / marked done? One line is plenty. (Confirmed with broker · Client said skip · See MOL bid for reference …)"
              className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-gray-500 outline-none focus:border-orange-400/30 resize-y min-h-[64px]"
              rows={3}
            />
            <p className="text-[11px]" style={{ color: "#6B7280" }}>
              Lives only in Callaloo. For team chat, use Slack/Teams — copy the row link below.
            </p>
          </section>

          {/* Activity log */}
          <section className="space-y-2">
            <FieldLabel>
              <span className="inline-flex items-center gap-1.5">
                <Clock size={11} />
                Activity
              </span>
            </FieldLabel>
            {activityLoading ? (
              <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
                <Loader2 size={12} className="animate-spin" />
                Loading…
              </div>
            ) : activity.length === 0 ? (
              <p className="text-xs py-2" style={{ color: "#6B7280" }}>
                No activity yet. Status changes, owner changes, and N/A toggles will show here.
              </p>
            ) : (
              <ol className="space-y-1.5">
                {activity.map((a) => (
                  <ActivityRow
                    key={a.id}
                    activity={a}
                    actor={a.actorUserId ? (usersById.get(a.actorUserId) ?? null) : null}
                  />
                ))}
              </ol>
            )}
          </section>
        </div>

        {/* Footer */}
        <footer
          className="px-5 py-3 border-t flex items-center justify-between"
          style={{ borderColor: "rgba(229,231,235,0.08)" }}
        >
          <button
            onClick={handleCopyLink}
            className="inline-flex items-center gap-1.5 text-xs text-gray-300 hover:text-white px-2 py-1.5 rounded-lg hover:bg-white/5"
            title="Copy link to this requirement"
          >
            <LinkIcon size={12} />
            Copy link
          </button>
          <button
            onClick={onDelete}
            className="inline-flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 px-2 py-1.5 rounded-lg hover:bg-red-500/10"
            title="Delete this row"
          >
            <Trash2 size={12} />
            Delete row
          </button>
        </footer>
      </aside>
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Small banner at the top of the side panel for rows synced to an external
 * PM tool. Surfaces last-synced timestamp and (when we add the URL to the
 * matrix item shape) an "Open in [tool]" link. For now we surface what we
 * have on the row.
 */
function SyncedBanner({ item }: { item: ResponseMatrixItem }) {
  const taskId = item.externalTaskId ?? item.teamworkTaskId;
  const synced = item.externalSyncedAt;
  return (
    <div
      className="rounded-lg border px-3 py-2 flex items-center gap-3"
      style={{
        backgroundColor: "rgba(34,197,94,0.06)",
        borderColor: "rgba(34,197,94,0.18)",
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: "#22c55e" }}
      />
      <div className="flex-1 min-w-0 text-xs">
        <p className="text-white">Synced to your project-management tool</p>
        <p className="mt-0.5" style={{ color: "#9CA3AF" }}>
          {synced ? `Last synced ${formatRelativeTime(synced)}` : `Task #${taskId}`}. Status and
          owner are managed there.
        </p>
      </div>
      {taskId && (
        <button
          onClick={() => {
            // The connector knows how to build a deep link, but we don't
            // have that here. As a v1 fallback, we don't open anything;
            // the side panel's footer can host the proper "Open in [tool]"
            // link once we ship the integration metadata in the API
            // response. Stub for now so the affordance is visible.
          }}
          className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-white px-2 py-1 rounded-md hover:bg-white/5 flex-shrink-0"
          title="Open in your PM tool (link wired in next pass)"
        >
          <ExternalLink size={11} />
          Open task
        </button>
      )}
    </div>
  );
}

function formatRelativeTime(iso: string): string {
  const d = new Date(iso);
  const min = Math.round((Date.now() - d.getTime()) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString();
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-[10px] uppercase tracking-wider font-semibold mb-1.5"
      style={{ color: "#6B7280" }}
    >
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Activity log row renderer
// ---------------------------------------------------------------------------

function ActivityRow({
  activity,
  actor,
}: {
  activity: MatrixItemActivity;
  actor: OrgUserSummary | null;
}) {
  const text = renderActivityText(activity, actor);
  const ts = formatActivityTimestamp(activity.createdAt);
  return (
    <li className="flex items-start gap-2 text-xs">
      <Avatar user={actor} size={18} />
      <div className="flex-1 min-w-0">
        <p style={{ color: "#D1D5DB" }}>{text}</p>
        <p className="mt-0.5 text-[10px]" style={{ color: "#6B7280" }}>
          {ts}
        </p>
      </div>
    </li>
  );
}

function actorName(actor: OrgUserSummary | null, kind: MatrixActivityKind): string {
  if (actor) return userLabel(actor);
  // Webhook-driven changes (e.g., Teamwork) come back actor-less. Use a
  // friendly fallback so the timeline isn't littered with "Unknown".
  if (kind === "teamwork_synced") return "Teamwork";
  return "System";
}

function renderActivityText(a: MatrixItemActivity, actor: OrgUserSummary | null): string {
  const who = actorName(actor, a.kind);
  const p = a.payload ?? {};
  switch (a.kind) {
    case "created":
      return p.source === "parser"
        ? `Parser added this requirement.`
        : `${who} added this requirement.`;
    case "status_changed":
      return `${who} changed status from ${labelFromStatusValue(p.from)} to ${labelFromStatusValue(p.to)}.`;
    case "owner_changed":
      if (!p.toUserId && p.fromUserId) return `${who} unassigned.`;
      if (p.toUserId && !p.fromUserId) return `${who} assigned to a teammate.`;
      return `${who} reassigned the owner.`;
    case "priority_changed":
      return `${who} set priority to ${String(p.to)}.`;
    case "type_changed":
      return `${who} changed type from ${String(p.from)} to ${String(p.to)}.`;
    case "na_toggled":
      return p.notApplicable ? `${who} marked not applicable.` : `${who} marked applicable again.`;
    case "notes_updated":
      return `${who} updated decision notes.`;
    case "due_date_changed":
      return p.to ? `${who} set due date to ${String(p.to)}.` : `${who} cleared the due date.`;
    case "linked_to_section":
      return `${who} linked a draft section.`;
    case "teamwork_synced":
      return `Synced to Teamwork.`;
    default:
      return `${who} updated this requirement.`;
  }
}

function labelFromStatusValue(v: unknown): string {
  // The renderer doesn't know the row's type, so we render the wire-level
  // status as a humanized fallback. Side-panel-only — the row dropdown
  // already shows the type-aware label live.
  if (typeof v !== "string") return String(v);
  switch (v) {
    case "not_started":
      return "Pending";
    case "in_progress":
      return "In Progress";
    case "draft_ready":
      return "Draft Ready";
    case "in_review":
      return "In Review";
    case "complete":
      return "Complete";
    default:
      return v;
  }
}

function formatActivityTimestamp(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
